/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-refresh/only-export-components */
import { createContext, useState, useCallback, ReactNode } from 'react';
import { Task, Milestone } from '../types';
import { useProjects } from '../hooks/useProjects';

// Fallback for when useProjects fails (e.g., during HMR or provider ordering issues)
const emptyProjectsReturn = {
  projects: [] as ReturnType<typeof useProjects>['projects'],
  tasks: [] as Task[],
  milestones: [] as Milestone[],
  updateTask: (_id: string, _updates: Partial<Task>) => { },
  updateMilestone: (_id: string, _updates: Partial<Milestone>) => { },
  refetch: () => { },
};

type ProjectsReturn = typeof emptyProjectsReturn | ReturnType<typeof useProjects>;

interface DataSyncContextType {
  // Shared read data (single source from useProjects)
  projects: ReturnType<typeof useProjects>['projects'];
  // Shared read data (single source from useProjects)
  tasks: Task[];
  milestones: Milestone[];
  /** Read state for summaries; failed or pending reads must not become zero totals. */
  readState?: { loading: boolean; error: string | null };

  // Sync methods
  syncTaskUpdate: (taskId: string, updates: Partial<Task>) => void;
  syncMilestoneUpdate: (milestoneId: string, updates: Partial<Milestone>) => void;
  syncTaskToMilestone: (taskId: string, milestoneId: string | undefined) => void;

  // Force refresh methods
  refreshAll: () => void;
  refreshTasks: () => void;
  refreshMilestones: () => void;

  // Last update tracking
  lastTaskUpdate: Date | null;
  lastMilestoneUpdate: Date | null;

  // Subscription for real-time updates
  onTaskUpdate: (callback: (task: Task) => void) => () => void;
  onMilestoneUpdate: (callback: (milestone: Milestone) => void) => () => void;
}

export const DataSyncContext = createContext<DataSyncContextType | null>(null);

interface DataSyncProviderProps {
  children: ReactNode;
}

export const DataSyncProvider = ({ children }: DataSyncProviderProps) => {
  // Always call useProjects unconditionally (rules-of-hooks requirement).
  // The hook itself handles missing providers gracefully.
  const projectsData: ProjectsReturn = useProjects();

  const {
    projects,
    tasks,
    milestones,
    updateTask,
    updateMilestone,
    refetch,
  } = projectsData;

  const [lastTaskUpdate, setLastTaskUpdate] = useState<Date | null>(null);
  const [lastMilestoneUpdate, setLastMilestoneUpdate] = useState<Date | null>(null);
  const [taskUpdateCallbacks, setTaskUpdateCallbacks] = useState<Set<(task: Task) => void>>(new Set());
  const [milestoneUpdateCallbacks, setMilestoneUpdateCallbacks] = useState<Set<(milestone: Milestone) => void>>(new Set());

  // Sync task update across all components
  const syncTaskUpdate = (taskId: string, updates: Partial<Task>) => {
    console.log(`[DataSync] Syncing task update: ${taskId}`, updates);

    // Update the task
    updateTask(taskId, updates);
    setLastTaskUpdate(new Date());

    // Find the updated task
    const updatedTask = tasks.find(t => t.id === taskId);
    if (updatedTask) {
      const finalTask = { ...updatedTask, ...updates };

      // Notify all subscribers
      taskUpdateCallbacks.forEach(callback => callback(finalTask));

      // Note: Milestone progress is now derived from tasks at render time,
      // so we don't need to sync milestone progress manually
    }
  };

  // Sync milestone update across all components
  const syncMilestoneUpdate = (milestoneId: string, updates: Partial<Milestone>) => {
    console.log(`[DataSync] Syncing milestone update: ${milestoneId}`, updates);

    updateMilestone(milestoneId, updates);
    setLastMilestoneUpdate(new Date());

    // Find the updated milestone
    const updatedMilestone = milestones.find(m => m.id === milestoneId);
    if (updatedMilestone) {
      const finalMilestone = { ...updatedMilestone, ...updates };

      // Notify all subscribers
      milestoneUpdateCallbacks.forEach(callback => callback(finalMilestone));
    }
  };

  // Sync task milestone assignment
  const syncTaskToMilestone = (taskId: string, milestoneId: string | undefined) => {
    console.log(`[DataSync] Syncing task milestone assignment: ${taskId} -> ${milestoneId}`);

    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    // Update task with new milestone assignment
    // Progress is derived at render time, no manual sync needed
    syncTaskUpdate(taskId, { milestoneId });
  };

  // Force refresh methods - memoized to prevent infinite loops
  const refreshAll = useCallback(() => {
    refetch();
    setLastTaskUpdate(new Date());
    setLastMilestoneUpdate(new Date());
    console.log('[DataSync] Force refreshing all data');
  }, [refetch]);

  const refreshTasks = useCallback(() => {
    setLastTaskUpdate(new Date());
    console.log('[DataSync] Force refreshing tasks');
  }, []);

  const refreshMilestones = useCallback(() => {
    setLastMilestoneUpdate(new Date());
    console.log('[DataSync] Force refreshing milestones');
  }, []);

  // Subscription methods
  const onTaskUpdate = (callback: (task: Task) => void) => {
    setTaskUpdateCallbacks(prev => new Set(prev).add(callback));

    // Return unsubscribe function
    return () => {
      setTaskUpdateCallbacks(prev => {
        const next = new Set(prev);
        next.delete(callback);
        return next;
      });
    };
  };

  const onMilestoneUpdate = (callback: (milestone: Milestone) => void) => {
    setMilestoneUpdateCallbacks(prev => new Set(prev).add(callback));

    // Return unsubscribe function
    return () => {
      setMilestoneUpdateCallbacks(prev => {
        const next = new Set(prev);
        next.delete(callback);
        return next;
      });
    };
  };

  const contextValue: DataSyncContextType = {
    projects,
    tasks,
    milestones,
    readState: {
      loading: 'loading' in projectsData ? projectsData.loading : true,
      error: 'error' in projectsData ? projectsData.error : null,
    },
    syncTaskUpdate,
    syncMilestoneUpdate,
    syncTaskToMilestone,
    refreshAll,
    refreshTasks,
    refreshMilestones,
    lastTaskUpdate,
    lastMilestoneUpdate,
    onTaskUpdate,
    onMilestoneUpdate
  };

  return (
    <DataSyncContext.Provider value={contextValue}>
      {children}
    </DataSyncContext.Provider>
  );
};

